import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ScrapCauseDto } from 'src/app/shared/dto/scrap-cause.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ScrapCauseService } from '../../services/scrap-cause.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-scrap-causes',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './scrap-causes.component.html',
    styleUrl: './scrap-causes.component.scss'
})
export class ScrapCausesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    causeSelected: ScrapCauseDto = new ScrapCauseDto();
    scrapCauses: ScrapCauseDto[] = [];
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        text: FormControl<string>;
    }>;

    private readonly causesService = inject(ScrapCauseService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('scrapCauses', '');
        this.validateForm = this.fb.group({
            text: ['', [Validators.required]]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.causesService.setupWebSocket();
        this.causesService.scrapCauses$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (causes: ScrapCauseDto[]) => {
                    this.scrapCauses = causes;
                    this.causeSelected = this.scrapCauses.find(sc => sc.id === this.causeSelected.id) || new ScrapCauseDto();
                },
                error: () => {
                    this.message.error('Error cargando causas de tara');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.causesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.causeSelected = { id: 0, text: '' };
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva causa de tara';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: ScrapCauseDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.causeSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo causa ' + this.causeSelected?.text;
        this.fieldsReadOnly = true;
        this.setData();
        this.openDrawer();
    }

    edit(data: ScrapCauseDto) {
        this.causeSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando causa ' + this.causeSelected?.text;
        this.fieldsReadOnly = false;
        this.setData();
        this.openDrawer();
    }

    setData() {
        // This does nothing, it's just for consistency
    }

    cancelEditing() {
        this.closeDrawer();
    }

    delete(id: number) {
        if (window.confirm('¿Estás seguro de que quieres eliminar esta causa de tara? Esta acción no se puede deshacer.')) {
            this.causesService.delete(id).subscribe({
                next: (cause: ScrapCauseDto) => {
                    try {
                        if (cause == undefined) {
                            this.message.create('error', `No se ha encontrado la causa de tara ${id}`);

                            return;
                        }

                        const index = this.scrapCauses.findIndex((p) => p.id === id);

                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar la causa ${cause.text} con id ${id}, causa de tara no encontrada`);

                            return;
                        }

                        this.scrapCauses.splice(index, 1);
                        const data = [...this.scrapCauses];
                        this.scrapCauses = data;
                        this.message.create('success', 'Causa de tara eliminado correctamente',);
                    } catch (error: any) {
                        this.message.create('error', `Error eliminando causa de tara. Error: ${error.message}`);
                    }
                },
                error: (error) => {
                    this.message.create('error', `Error eliminando causa de tara, error de servidor. Error: ${error.message}`);
                }
            });
        } else {
            console.log('Cancelando eliminación de causa de tara ' + id,);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.causeSelected = new ScrapCauseDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    submitForm() {
        try {
            this.isSaving = true;

            const customCauseDto: ScrapCauseDto = {
                id: this.causeSelected.id,
                text: this.validateForm.controls.text.value ? this.validateForm.controls.text.value : ''
            };

            if (!this.validForm(customCauseDto)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                this.causesService.save(customCauseDto).subscribe({
                    next: (cause: ScrapCauseDto) => {
                        try {
                            if (cause == undefined) {
                                this.isSaving = false;
                                this.message.create('error', `Error creando causa de tara`);

                                return;
                            }

                            this.scrapCauses.push(cause);
                            const data = [...this.scrapCauses];
                            this.scrapCauses = data;
                            
                            this.isSaving = false;
                            this.message.create('success', 'Causa de tara creada correctamente');
                            this.closeDrawer();
                        } catch (error: any) {
                            this.isSaving = false;
                            this.message.create('error', `Error creando causa de tara. Error: ${error.message}`);
                        }
                    },
                    error: (error: any) => {
                        this.isSaving = false;
                        this.message.create('error', `Error creando causa de tara, error de servidor. Error: ${error.message}`);
                    }
                });
            } else if (this.drawerMode === DrawerMode.EDIT) {
                this.causesService.update(customCauseDto.id, customCauseDto).subscribe({
                    next: (cause: ScrapCauseDto) => {
                        try {
                            if (cause == undefined) {
                                this.isSaving = false;
                                this.message.create('error', 'Error creando causas de tara');

                                return;
                            }

                            const index = this.scrapCauses.findIndex(
                                (r) => r.id === cause.id
                            );

                            if (index < 0) {
                                this.isSaving = false;
                                this.message.create('error', `No se ha podido actualizar la causa ${customCauseDto.text} con id ${customCauseDto.id}, causa de tara no encontrada`);

                                return;
                            }

                            this.scrapCauses[index] = cause;
                            const data = [...this.scrapCauses];
                            this.scrapCauses = data;

                            this.isSaving = false;
                            this.message.create('success', 'Causa de tara actualizada correctamente');

                            this.closeDrawer();
                        } catch (error: any) {
                            this.isSaving = false;
                            this.message.create('error', `Error actualizando causa de tara. Error: ${error.message}`);
                        }
                    },
                    error: (error: any) => {
                        this.isSaving = false;
                        this.message.create('error', `Error actualizando causa de tara, error de servidor. Error: ${error.message}`);
                    }
                });
            }
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }

    validForm(cause: ScrapCauseDto): boolean {
        if (!cause.text) {
            this.message.create(
                'error',
                'El texto de la causa de tara es obligatorio',
            );
            return false;
        }
        return true;
    }
}
